import { combineEpics, Epic, ofType } from 'redux-observable';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { getType } from 'typesafe-actions';

import { EpicDeps } from '../../../service/StoreFactory';
import { State } from '../../../typings/state';
import { CanvasAction } from '../../common/actions';
import { Bundle } from '../../common/requestsTypings';
import { openModal } from '../../CreativeImageCropModal/actions';
import { CreativeOptionsData } from '../../CreativeOptions/typings';
import {
    AddCreativeAction,
    cancelPreviewsModal,
    crop,
    CropAction,
    initCommonCreative,
    initCommonCreativeSuccess,
    resetPreviewsModal
} from '../actions';
import { showAddCreativeError, showAddCreativeErrorAction } from '../epics';
import { CommonCreativeItem } from '../typings';

import {
    getCommonCreativeState,
    getCommonItemsWithCropParameteres,
    getCreativeSizeData,
    getCroppedFileIds,
    getCropSizesValue,
    isCommonOptionsAvailable
} from './helpers';
import { mapCommonCreativeFromApi, mapCommonCreativeFromIdeaApi, mapCommonCreativeFromPresetApi } from './mappers';

const initDataEpic: Epic<AddCreativeAction, AddCreativeAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(initCommonCreative)),
        switchMap(
            (): Observable<AddCreativeAction> => {
                const {
                    sharedData,
                    canvas: {
                        commonData: { ideaId },
                        addCreative
                    }
                } = store$.value.canvasEditor;
                const tld = store$.value.commonData.tld;
                const { presetId, id } = addCreative;
                const api = deps.canvasApi.addCommonCreative;
                const commonOptionsAvailable = isCommonOptionsAvailable(sharedData.creativeType);

                if (ideaId && presetId) {
                    return forkJoin(
                        api.fetchIdeaData(ideaId, presetId, sharedData),
                        api.fetchDisclaimers(sharedData)
                    ).pipe(
                        map(([preset, disclaimers]) =>
                            mapCommonCreativeFromIdeaApi(preset, disclaimers, tld, commonOptionsAvailable)
                        ),
                        map(initCommonCreativeSuccess),
                        catchError(error => showAddCreativeError(error.message || error.getNativeError().message))
                    );
                }

                if (id) {
                    return forkJoin(api.fetchBatchData(id, sharedData), api.fetchDisclaimers(sharedData)).pipe(
                        map(([batch, disclaimers]) =>
                            mapCommonCreativeFromApi(batch, disclaimers, tld, commonOptionsAvailable)
                        ),
                        switchMap(
                            (result: {
                                options: CreativeOptionsData;
                                items: CommonCreativeItem[];
                                bundle: Bundle;
                                presetId?: string;
                                batchId?: string;
                            }): Observable<AddCreativeAction> => {
                                const croppedFiles = getCroppedFileIds(result.items);

                                if (!croppedFiles.length) {
                                    return of(initCommonCreativeSuccess(result));
                                }

                                return forkJoin(
                                    croppedFiles.map(file => api.fetchFileData(file.croppedFileId, sharedData))
                                ).pipe(
                                    map(response => {
                                        result.items = getCommonItemsWithCropParameteres(
                                            result.items,
                                            croppedFiles,
                                            response
                                        );

                                        return result;
                                    }),
                                    map(initCommonCreativeSuccess)
                                );
                            }
                        ),
                        catchError(error => showAddCreativeError(error.message || error.getNativeError().message))
                    );
                }

                if (presetId) {
                    return forkJoin(
                        api.fetchPresetData(Number(presetId), sharedData),
                        api.fetchDisclaimers(sharedData)
                    ).pipe(
                        map(([preset, disclaimers]) =>
                            mapCommonCreativeFromPresetApi(preset, disclaimers, tld, commonOptionsAvailable)
                        ),
                        map(initCommonCreativeSuccess),
                        catchError(error => showAddCreativeError(error.message || error.getNativeError().message))
                    );
                }

                return showAddCreativeError();
            }
        )
    );
};

const recropAllEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(cancelPreviewsModal)),
        map(() => {
            const addCreative = getCommonCreativeState(store$.value.canvasEditor.canvas.addCreative);
            if (!addCreative) {
                return showAddCreativeErrorAction();
            }

            const { currentId, previewItems, editingFileInfo } = addCreative;

            if (!editingFileInfo) {
                return resetPreviewsModal();
            }

            if (currentId === undefined || !previewItems) {
                return showAddCreativeErrorAction();
            }

            return openModal(
                {
                    src: editingFileInfo.cropSourceUrl,
                    resourceId: editingFileInfo.fileId
                },
                getCreativeSizeData(currentId, previewItems),
                true,
                getCropSizesValue(previewItems)
            );
        })
    );
};

const recropEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(crop)),
        map((action: CropAction) => {
            const addCreative = getCommonCreativeState(store$.value.canvasEditor.canvas.addCreative);
            if (!addCreative) {
                return showAddCreativeErrorAction();
            }

            const { previewItems, editingFileInfo } = addCreative;

            if (!previewItems || !editingFileInfo) {
                return showAddCreativeErrorAction();
            }

            return openModal(
                {
                    src: editingFileInfo.cropSourceUrl,
                    resourceId: editingFileInfo.fileId
                },
                getCreativeSizeData(action.id, previewItems),
                true,
                getCropSizesValue(previewItems)
            );
        })
    );
};

export const addCommonCreativeEpics = combineEpics(initDataEpic, recropAllEpic, recropEpic);
